import { computed } from 'vue'
import { COLLECTIONS, type AlgoliaLikeRequestOptions } from '~/lib/search/typesense'

/**
 * Replacements for @nuxtjs/algolia's useAlgoliaSearch / useAsyncAlgoliaSearch on Typesense.
 * Same signatures and Algolia-shaped results as before the switch.
 */

interface SearchArgs {
  query: string
  requestOptions?: AlgoliaLikeRequestOptions
}

export function useSearch(indexName?: string) {
  const index = indexName || COLLECTIONS.products
  const result = useState<any>(`${index}-search-result`, () => null)

  const search = async ({ query, requestOptions }: SearchArgs) => {
    const searchResult = await useTypesenseSearch().search(index, query, requestOptions)
    result.value = searchResult
    return searchResult
  }

  return {
    result: computed(() => result.value),
    search
  }
}

/** Short stable hash of a string (djb2), for data keys */
const hashString = (value: string) => {
  let hash = 5381
  for (let i = 0; i < value.length; i++) hash = Math.imul(hash, 33) ^ value.charCodeAt(i)
  return (hash >>> 0).toString(36)
}

/**
 * The key includes a hash of the request. Since Nuxt 4, calls with the same key share one result, and a key that
 * already has a result is not fetched again: the same key with other filters (the cart rule check per SKU, the next
 * product's carousels on client navigation) would get the previous result (Nuxt 4 upgrade 4.11.2026).
 */
export async function useAsyncSearch({ query, requestOptions, indexName, key }: SearchArgs & { indexName?: string, key?: string }) {
  const index = indexName || COLLECTIONS.products
  const typesense = useTypesenseSearch()
  const requestHash = hashString(JSON.stringify([query, requestOptions ?? null]))

  return useAsyncData(`${index}-async-search-result-${key ?? ''}-${requestHash}`, () => typesense.search(index, query, requestOptions))
}

/** Several searches in one multi_search request (quick search panel). Call in setup; the returned function runs the searches. */
export function useMultiSearch(indexNames: (string | undefined)[]) {
  const indexes = indexNames.map(name => name || COLLECTIONS.products)
  const typesense = useTypesenseSearch()
  return (requests: SearchArgs[]) => typesense.multiSearch(requests.map((request, i) => ({
    indexName: indexes[i],
    query: request.query,
    options: request.requestOptions
  })))
}
